import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { authMeCall } from "@/lib/api";

const AuthCtx = createContext(null);

// Internal escape hatch for the loading state — has to sit ABOVE the total
// authMeCall budget (30s + 1.5s backoff + 15s retry = 46.5s) but BELOW the
// AuthHealthMonitor watchdog (60s). Raised from 20s Jul 3 2026 alongside
// the mobile-data resilience fix so it no longer races with a legitimate
// in-flight retry.
const AUTH_HARD_WALL_MS = 55000;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("stellar_token");
    if (!token) { setUser(null); setLoading(false); return null; }
    // Hard wall: even if axios timeout / retry somehow doesn't fire — bundle
    // chunk failure on poor mobile network, browser extension intercept,
    // service worker quirk — guarantee `loading` flips to false within the
    // hard-wall budget so users never see the "Aligning..." screen forever.
    // May 22 2026 fix (originally 20s), raised to 55s Jul 3 2026 to sit
    // above the /auth/me retry budget but below the 60s watchdog.
    const hardWall = setTimeout(() => setLoading(false), AUTH_HARD_WALL_MS);
    let freshUser = null;
    try {
      const { data } = await authMeCall();
      setUser(data.user);
      freshUser = data.user;
    } catch (err) {
      // BUG FIX Feb 24 2026: previously this catch wiped the token on
      // ANY error — including gateway 502/503/504 and network blips —
      // which logged users out whenever a transient backend issue
      // surfaced (e.g., during a slow /ask/v2 call when the same pod
      // would also be slow on /auth/me, OR when AskV2's post-error
      // refresh() ran while the pod was degraded). Only 401/403
      // indicate an invalid token; all other errors mean "we don't
      // know" and the session must be preserved so the user keeps
      // their place. Surfaced after a 30s gateway-timeout incident
      // chained into auto-signouts on /api/ask/v2 failures.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("stellar_token");
        setUser(null);
      } else {
        // Transient backend / network blip — keep the cached user
        // state intact. If we never loaded a user yet (cold mount
        // hitting a 5xx), leave `user` as null but DON'T wipe the
        // token; next refresh tick (visibility/focus) will retry.
        console.warn("[auth/refresh] transient error, NOT signing out", { status, msg: err?.message });
      }
    }
    clearTimeout(hardWall);
    setLoading(false);
    return freshUser;
  }, []);

  // Auto-refresh credits when the user comes back to the tab (e.g. after a
  // Stripe checkout redirect in a popup or another tab). Without this, a
  // newly-paid user keeps seeing their old "0 credits" badge until they
  // manually refresh — surfaced May 19 2026 production incident
  // (natlol123446@gmail.com paid via Stripe; webhook landed credits in DB
  // but the cached `user` in React still showed 0).
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible" && localStorage.getItem("stellar_token")) {
        refresh();
      }
    };
    // NOTE (Jul 3 2026): The `focus` listener was removed. It duplicated
    // `visibilitychange` on every alt-tab and, on flaky mobile networks
    // where 5G takes 2-5s to reconnect after screen wake, kicked off a
    // fresh /auth/me round-trip that could then time out and cascade into
    // a false "session expired" logout. `visibilitychange` alone covers
    // the real use case (returning to the tab after a Stripe checkout
    // popup or another app) without piling on redundant refreshes.
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback (self-hosted Google flow,
    // May 27 2026), skip the initial /me check. AuthCallback owns the token
    // adoption and will trigger refresh once the JWT is stashed.
    if (typeof window !== "undefined" &&
        window.location.pathname === "/auth/callback" &&
        (new URLSearchParams(window.location.search).get("code") ||
         new URLSearchParams(window.location.search).get("error"))) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("stellar_token", data.token);
    setUser(data.user);
    return data.user;
  };
  const signup = async (email, password, name, referral_code = null) => {
    const body = { email, password, name };
    if (referral_code) body.referral_code = referral_code;
    const { data } = await api.post("/auth/signup", body);
    localStorage.setItem("stellar_token", data.token);
    setUser(data.user);
    if (data.referral_bonus_applied) {
      // Surface a one-shot flag the next page can read for a celebratory toast
      try { sessionStorage.setItem("bhrigu_ref_bonus_just_applied", "1"); } catch (e) {/**/}
    }
    // Jul 1 2026: email/password users must verify to unlock the 3-credit
    // welcome bonus. Return the full response so the Signup page can render
    // the "check your email" state instead of routing to the dashboard.
    return { user: data.user, verification_email_sent: !!data.verification_email_sent,
             signup_bonus_credits: data.signup_bonus_credits || 3 };
  };
  // Self-hosted Google OAuth (May 27 2026). AuthCallback completes the
  // code exchange via POST /api/auth/google/exchange and receives both
  // the JWT and the hydrated user record in one round-trip. This helper
  // simply stashes both into context state — no extra /me call needed.
  const adoptSession = (token, userData) => {
    if (!token) throw new Error("missing token");
    localStorage.setItem("stellar_token", token);
    if (userData) setUser(userData);
  };
  const logout = () => {
    localStorage.removeItem("stellar_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, signup, adoptSession, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
